/* eslint-disable camelcase */
import { observable, action, computed } from "mobx";
import uuidv4 from "uuid/v4";
import { slugify } from "~/utils/StringHelper";
import CustomFieldType from "~/constants/UiMeet3d/GreetingBot/CustomFieldType";

class CustomField {
  @observable id;

  @observable isSelected;

  @observable type;

  @observable name;

  @observable label_en;

  @observable label_fr;

  @observable value_en;

  @observable value_fr;

  @observable mandatory = false;

  constructor({
    id = null,
    type = null,
    name = null,
    label_en = null,
    label_fr = null,
    mandatory = false,
    value_en = null,
    value_fr = null,
    isSelected = false,
  }) {
    this.id = id || uuidv4();
    this.type = type;
    this.name = name;
    this.label_en = label_en;
    this.label_fr = label_fr;
    this.mandatory = mandatory;
    this.value_en = value_en;
    this.value_fr = value_fr;
    this.isSelected = isSelected;

    // only validate type if a value was given.
    if (type && !this.isTypeValid(type)) {
      throw new Error(`Invalid field type ${type}`);
    }
  }

  @action
  setType(type) {
    this.type = type;

    // only validate type if a value was given.
    if (type && !this.isTypeValid(type)) {
      throw new Error(`Invalid field type ${type}`);
    }
  }

  @action
  setLabelEn(label_en) {
    this.label_en = label_en;
  }

  @action
  setLabelFr(label_fr) {
    this.label_fr = label_fr;
  }

  @action
  setName(name) {
    this.name = name;
  }

  @action
  setMandatory(mandatory) {
    this.mandatory = mandatory;
  }

  @action
  setValueEn(value_en) {
    this.value_en = value_en;
  }

  @action
  setValueFr(value_fr) {
    this.value_fr = value_fr;
  }

  @computed
  get nameCoalesced() {
    return this.name || this.nameFromLabel;
  }

  @computed
  get nameFromLabel() {
    if (this.label_en || this.label_fr) {
      return slugify(this.label_en || this.label_fr, "_");
    }

    return null;
  }

  @computed
  get labelCoalesced() {
    if ((this.label_en || "").trim() !== "") {
      return this.label_en;
    }

    if ((this.label_fr || "").trim() !== "") {
      return this.label_fr;
    }

    return null;
  }

  @action
  setIsSelected(isSelected) {
    this.isSelected = isSelected;
  }

  toJS() {
    return {
      type: this.type || null,
      name: this.nameCoalesced || null,
      label: {
        en: this.label_en || null,
        fr: this.label_fr || null,
      },
      rules: {
        mandatory: this.mandatory,
      },
      value: {
        en: this.value_en || null,
        fr: this.value_fr || null,
      },
    };
  }

  static fromJS(data) {
    return new CustomField({
      type: data.type,
      name: data.name,
      label_en: data.label.en,
      label_fr: data.label.fr,
      mandatory: data.rules.mandatory,
      value_en: data.value.en,
      value_fr: data.value.fr,
    });
  }

  isValid() {
    // All fields types require a type
    if (!this.isTypeValid(this.type)) {
      return false;
    }

    // All fields require a label
    if (!this.labelCoalesced) {
      return false;
    }

    // Field name wil be computed from the field's label so if label is set then a name is guaranteed to be available.
    return true;
  }

  isTypeValid(type) {
    return Object.values(CustomFieldType).indexOf(type) !== -1;
  }

  @computed
  get hash() {
    return `${this.type}_${this.label_en}_${this.label_fr}_${this.name}_${this.mandatory}_${this.value_en}_${this.value_fr}`;
  }
}

export default CustomField;
